import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { metaAdStatusLabels } from '@borradh-workspace/labels';
import type { ReactNode } from 'react';

export function Hint({
  children,
  label,
}: { children: ReactNode; label: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        align="start"
        sideOffset={6}
        collisionPadding={12}
        className="z-[80] max-w-[min(20rem,calc(100vw-1.5rem))]"
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

const EXTRA_STATUSES: Record<string, string> = {
  campaign_paused: 'Campaign paused',
  adset_paused: 'Ad set paused',
  pending_review: 'In review',
  unknown: 'Not synced',
};
export const statusLabel = (status: string) =>
  EXTRA_STATUSES[status] ??
  metaAdStatusLabels[status as keyof typeof metaAdStatusLabels] ??
  status.replaceAll('_', ' ');

export function DeliveryBadge({ status }: { status: string }) {
  return (
    <Badge variant="outline" className="gap-1.5 font-normal">
      <span
        aria-hidden
        className={cn(
          'size-1.5 rounded-full',
          status === 'active'
            ? 'bg-emerald-600 dark:bg-emerald-400'
            : ['error', 'rejected', 'disapproved', 'with_issues'].includes(
                  status
                )
              ? 'bg-destructive'
              : status.includes('review')
                ? 'bg-amber-500'
                : 'bg-muted-foreground'
        )}
      />
      {statusLabel(status)}
    </Badge>
  );
}

export function FilterSelect({
  value,
  onChange,
  label,
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={label} className="min-w-28 max-sm:flex-1">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </Select>
  );
}

export function Stat({
  label,
  value,
  note,
  hint,
}: { label: string; value: string; note: string; hint: string }) {
  return (
    <Card className="gap-0 py-5 shadow-none">
      <CardHeader className="gap-2 px-5">
        <CardDescription>
          <Hint label={hint}>
            <button
              type="button"
              className="cursor-help text-left underline decoration-dotted decoration-muted-foreground/50 underline-offset-4"
            >
              {label}
            </button>
          </Hint>
        </CardDescription>
        <CardTitle className="text-xl tracking-tight tabular-nums sm:text-3xl">
          {value}
        </CardTitle>
        <Hint label={hint}>
          <p className="text-xs text-muted-foreground">{note}</p>
        </Hint>
      </CardHeader>
    </Card>
  );
}

export function formatTimestamp(value: string | null) {
  if (!value) return 'Not synced';
  return `${new Intl.DateTimeFormat('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }).format(new Date(value))} UTC`;
}

export function currencyName(currency: string | null) {
  if (!currency) return 'Account currency is not available';
  try {
    return `${currency}: ${new Intl.DisplayNames(['en'], { type: 'currency' }).of(currency)}`;
  } catch {
    return currency;
  }
}
